/* App-wide error boundary.
 *
 * Without this, a single render error anywhere in the tree unmounts the WHOLE
 * app to a blank white screen with no way out — which on a doctor's installed
 * PWA looks exactly like "I tapped the prescription and nothing happened / it
 * went blank." This catches that error and shows a recoverable screen instead.
 *
 * Two behaviours:
 *   - Stale-asset failures (ChunkLoadError / "Loading chunk N failed" / failed
 *     dynamic import) usually mean the PWA is mid-session on an old build whose
 *     chunk was deleted by a redeploy. We self-heal by force-updating (bin the
 *     caches, drop the SW, reload past the HTTP cache) — once per session so we
 *     can never get stuck in a reload loop.
 *   - Any other error → a friendly card with the actual message plus "Reload
 *     app" (same force-update) and "Go back", so the user recovers without
 *     reinstalling and we get the real error text to diagnose the root cause.
 */
import React from "react";
import { forceUpdateNow, runningBuildId } from "@/serviceWorkerRegistration";

function isStaleAssetError(err) {
  const name = err?.name || "";
  const msg = String(err?.message || err || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /import.*module.*failed/i.test(msg)
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, recovering: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep the details in the console for field diagnosis.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info?.componentStack);

    // A stale bundle/chunk on an installed PWA is self-inflicted by a redeploy —
    // force a clean reload once so the device lands on the current build.
    if (isStaleAssetError(error)) {
      const guard = "emedbay_chunk_autoreload";
      try {
        if (!sessionStorage.getItem(guard)) {
          sessionStorage.setItem(guard, "1");
          this.setState({ recovering: true });
          forceUpdateNow();
        }
      } catch {
        forceUpdateNow();
      }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.state.recovering) {
      return (
        <div style={wrap}>
          <div style={card}>
            <div style={{ fontSize: 15, color: "#334155" }}>Updating to the latest version…</div>
          </div>
        </div>
      );
    }

    const stale = isStaleAssetError(this.state.error);
    const msg = String(this.state.error?.message || this.state.error || "Unknown error");

    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
            Something went wrong
          </div>
          <div style={{ fontSize: 14, color: "#64748b", marginBottom: 16, lineHeight: 1.5 }}>
            {stale
              ? "This device is running an old version of the app. Reloading will update it."
              : "The screen hit an error and couldn't load. You can go back and try again, or reload the app."}
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <button style={primaryBtn} onClick={() => forceUpdateNow()}>Reload app</button>
            <button
              style={ghostBtn}
              onClick={() => {
                this.setState({ hasError: false, error: null });
                try { window.history.back(); } catch {}
              }}
            >
              Go back
            </button>
          </div>

          <details style={{ marginTop: 18, textAlign: "left" }}>
            <summary style={{ fontSize: 12, color: "#94a3b8", cursor: "pointer" }}>
              Technical details
            </summary>
            <pre style={pre}>{msg}</pre>
            <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 4 }}>
              build {runningBuildId?.() || "?"}
            </div>
          </details>
        </div>
      </div>
    );
  }
}

const wrap = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "#f1f5f9",
};
const card = {
  maxWidth: 420,
  width: "100%",
  background: "#fff",
  borderRadius: 20,
  padding: "32px 24px",
  textAlign: "center",
  boxShadow: "0 10px 30px rgba(2,6,23,.08)",
  border: "1px solid #e2e8f0",
};
const primaryBtn = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 12,
  padding: "10px 18px",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
};
const ghostBtn = {
  background: "#fff",
  color: "#334155",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  padding: "10px 18px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};
const pre = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontSize: 11,
  color: "#64748b",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: 10,
  marginTop: 8,
  maxHeight: 160,
  overflow: "auto",
};
